import type { ReactNode } from 'react';
import { useState } from 'react';
import type { Note } from '../../types';
import { useNotes, formatNoteDate } from '../../hooks/useNotes';

export interface NotesSectionProps {
  parentType: 'lead' | 'opportunity' | 'contact';
  parentId: string;
}

interface NoteItemProps {
  note: Note;
  onEdit: (noteId: string, content: string) => Promise<Note | null>;
  onDelete: (noteId: string) => Promise<boolean>;
  onTogglePin: (noteId: string) => Promise<Note | null>;
}

function NoteItem({ note, onEdit, onDelete, onTogglePin }: NoteItemProps): ReactNode {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    if (!editContent.trim()) return;
    const result = await onEdit(note.id, editContent.trim());
    if (result) {
      setIsEditing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this note?')) return;
    setIsDeleting(true);
    await onDelete(note.id);
    setIsDeleting(false);
  };

  return (
    <div className={`p-3 rounded-lg ${note.pinned ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
      {isEditing ? (
        <div>
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full h-24 resize-none text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => {
                setIsEditing(false);
                setEditContent(note.content);
              }}
              className="btn btn-secondary text-xs px-2 py-1"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editContent.trim()}
              className="btn btn-primary text-xs px-2 py-1"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm text-gray-700 whitespace-pre-wrap flex-1">
              {note.content}
            </p>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => onTogglePin(note.id)}
                className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                  note.pinned ? 'text-yellow-600' : 'text-gray-400'
                }`}
                title={note.pinned ? 'Unpin' : 'Pin'}
              >
                <svg className="w-4 h-4" fill={note.pinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
                title="Edit"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-400">
            {formatNoteDate(note.created_at)}
            {note.pinned && <span className="ml-2 text-yellow-600">📌 Pinned</span>}
          </div>
        </>
      )}
    </div>
  );
}

export function NotesSection({ parentType, parentId }: NotesSectionProps): ReactNode {
  const {
    notes,
    pinnedNotes,
    loading,
    error,
    hasMore,
    loadMore,
    createNote,
    updateNote,
    deleteNote,
    togglePin,
  } = useNotes({ parentType, parentId });

  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newNote.trim()) return;
    setIsSubmitting(true);
    const result = await createNote(newNote.trim());
    if (result) {
      setNewNote('');
    }
    setIsSubmitting(false);
  };

  if (loading && notes.length === 0 && pinnedNotes.length === 0) {
    return (
      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Notes</h2>
        <div className="animate-pulse space-y-3">
          <div className="h-20 bg-gray-100 rounded"></div>
          <div className="h-16 bg-gray-50 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Notes</h2>

      {/* New note input */}
      <div className="mb-4">
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note..."
          className="w-full h-24 resize-none"
        />
        <button
          onClick={handleSubmit}
          disabled={!newNote.trim() || isSubmitting}
          className="btn btn-primary w-full mt-2"
        >
          {isSubmitting ? 'Saving...' : 'Save Note'}
        </button>
      </div>

      {error && (
        <div className="text-red-600 text-sm mb-4">{error}</div>
      )}

      {/* Pinned notes */}
      {pinnedNotes.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            Pinned
          </h3>
          <div className="space-y-2">
            {pinnedNotes.map(note => (
              <NoteItem
                key={note.id}
                note={note}
                onEdit={updateNote}
                onDelete={deleteNote}
                onTogglePin={togglePin}
              />
            ))}
          </div>
        </div>
      )}

      {/* Regular notes */}
      {notes.length > 0 ? (
        <div className="space-y-2">
          {pinnedNotes.length > 0 && (
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Notes
            </h3>
          )}
          {notes.map(note => (
            <NoteItem
              key={note.id}
              note={note}
              onEdit={updateNote}
              onDelete={deleteNote}
              onTogglePin={togglePin}
            />
          ))}
          {hasMore && (
            <button
              onClick={loadMore}
              className="w-full text-center text-sm text-ds-primary hover:underline py-2"
            >
              Load more notes...
            </button>
          )}
        </div>
      ) : (
        pinnedNotes.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-4">No notes yet</p>
        )
      )}
    </div>
  );
}

export default NotesSection;
